import { useCallback, useMemo, useState } from 'react';
import type {
   CreateAlbumFormPayload,
   CreateAlbumWizardAlbumType,
   SerializedCreateAlbumWizardState,
} from './createAlbumWizardTypes';
import { DEFAULT_WIZARD_SERIALIZED } from './createAlbumWizardTypes';

export interface UseCreateAlbumWizardOptions {
   /** Hydrate from sessionStorage or similar */
   initial?: Partial<SerializedCreateAlbumWizardState>;
}

export function useCreateAlbumWizard(options: UseCreateAlbumWizardOptions = {}) {
   const mergedInitial = { ...DEFAULT_WIZARD_SERIALIZED, ...options.initial };
   const [activeStep, setActiveStep] = useState(mergedInitial.activeStep);
   const [selectedType, setSelectedType] = useState<CreateAlbumWizardAlbumType | null>(
      mergedInitial.selectedType
   );
   const [title, setTitle] = useState(mergedInitial.title);
   const [description, setDescription] = useState(mergedInitial.description);
   const [albumDate, setAlbumDate] = useState(mergedInitial.albumDate);
   const [enableParticipantParticipation, setEnableParticipantParticipation] = useState(
      mergedInitial.enableParticipantParticipation
   );
   const [pricePerPhoto, setPricePerPhoto] = useState(mergedInitial.pricePerPhoto);
   const [pricingModel, setPricingModel] = useState<'per_photo' | 'one_time' | 'donation'>(
      mergedInitial.pricingModel
   );
   const [paidAlbumAccessType, setPaidAlbumAccessType] = useState<'public' | 'private'>(
      mergedInitial.paidAlbumAccessType
   );

   const [error, setError] = useState('');
   const [showTitleError, setShowTitleError] = useState(false);

   const reset = useCallback(() => {
      setActiveStep(0);
      setSelectedType(null);
      setTitle('');
      setDescription('');
      setAlbumDate('');
      setEnableParticipantParticipation(true);
      setError('');
      setShowTitleError(false);
      setPricePerPhoto('5.00');
      setPricingModel('per_photo');
      setPaidAlbumAccessType('public');
   }, []);

   const serialize = useCallback((): SerializedCreateAlbumWizardState => {
      return {
         activeStep,
         selectedType,
         title,
         description,
         albumDate,
         enableParticipantParticipation,
         pricePerPhoto,
         pricingModel,
         paidAlbumAccessType,
      };
   }, [
      activeStep,
      selectedType,
      title,
      description,
      albumDate,
      enableParticipantParticipation,
      pricePerPhoto,
      pricingModel,
      paidAlbumAccessType,
   ]);

   const handleNext = useCallback(() => {
      if (activeStep === 0 && !selectedType) {
         setError('Please select an album type');
         return false;
      }
      if (activeStep === 1 && !title.trim()) {
         setError('Album title is required');
         setShowTitleError(true);
         return false;
      }
      if (activeStep === 1 && selectedType === 'public') {
         setActiveStep(2);
         setError('');
         setShowTitleError(false);
         return true;
      }
      setError('');
      setShowTitleError(false);
      setActiveStep((s) => s + 1);
      return true;
   }, [activeStep, selectedType, title]);

   const handleBack = useCallback(() => {
      setError('');
      setActiveStep((s) => Math.max(0, s - 1));
   }, []);

   const buildPayload = useCallback((): { payload?: CreateAlbumFormPayload; error?: string } => {
      if (!title.trim()) {
         return { error: 'Album title is required' };
      }
      if (!selectedType) {
         return { error: 'Please select an album type' };
      }

      const payload: CreateAlbumFormPayload = {
         title: title.trim(),
         description: description.trim() || '',
         type: selectedType,
         enableParticipantParticipation,
         albumDate: albumDate || undefined,
         ...(selectedType === 'paid' && {
            pricePerPhoto: parseFloat(pricePerPhoto) || 5.0,
            pricingModel,
            paidAlbumAccessType,
         }),
      };

      return { payload };
   }, [
      title,
      description,
      selectedType,
      enableParticipantParticipation,
      albumDate,
      pricePerPhoto,
      pricingModel,
      paidAlbumAccessType,
   ]);

   const stepSubtitle = useMemo(() => {
      if (activeStep === 0) return 'Choose Type';
      if (activeStep === 1) return 'Basic Info';
      if (selectedType === 'public') return 'Review';
      return 'Configure';
   }, [activeStep, selectedType]);

   return {
      activeStep,
      setActiveStep,
      selectedType,
      setSelectedType,
      title,
      setTitle,
      description,
      setDescription,
      albumDate,
      setAlbumDate,
      enableParticipantParticipation,
      setEnableParticipantParticipation,
      pricePerPhoto,
      setPricePerPhoto,
      pricingModel,
      setPricingModel,
      paidAlbumAccessType,
      setPaidAlbumAccessType,
      error,
      setError,
      showTitleError,
      setShowTitleError,
      reset,
      serialize,
      handleNext,
      handleBack,
      buildPayload,
      stepSubtitle,
   };
}

export type CreateAlbumWizardApi = ReturnType<typeof useCreateAlbumWizard>;
